import { API } from '@/lib/api';
import { useState } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { User, ArrowLeft, Lock, Shield, Trash2, Mail, Phone, Edit3, Save, AlertTriangle, Key, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";



const AccountManagementPage = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwords, setPasswords] = useState({ current: "", new: "", confirm: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await axios.post(`${API}/upload/profile-picture`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      if (refreshUser) await refreshUser();
      toast.success("Profile picture updated!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Upload failed"));
    }
    setUploadingPicture(false);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/user/profile`, { name, phone }, { withCredentials: true });
      toast.success("Profile updated");
      setEditing(false);
    } catch (err) { toast.error(getErrorMessage(err, "Update failed")); }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) { toast.error("Passwords don't match"); return; }
    if (passwords.new.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setChangingPassword(true);
    try {
      await axios.patch(`${API}/user/password`, { current_password: passwords.current, new_password: passwords.new }, { withCredentials: true });
      toast.success("Password changed successfully");
      setShowPasswordForm(false);
      setPasswords({ current: "", new: "", confirm: "" });
    } catch (err) { toast.error(getErrorMessage(err, "Password change failed")); }
    setChangingPassword(false);
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/user/account`, { withCredentials: true });
      toast.success("Account deleted");
      await logout();
      navigate("/");
    } catch (err) { toast.error(getErrorMessage(err, "Failed to delete account")); }
    setDeleting(false);
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="account-management-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-3xl mx-auto">
        <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Work Hub
        </Link>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl font-bold text-emerald-950 flex items-center gap-2 mb-6">
            <User className="w-6 h-6 text-emerald-600" /> Account Management
          </h1>

          {/* Profile Picture */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-4 flex flex-col items-center" data-testid="picture-section">
            <div className="relative group mb-3">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full object-cover border-4 border-stone-100" data-testid="profile-avatar" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center border-4 border-stone-100" data-testid="profile-avatar">
                  <User className="w-10 h-10 text-emerald-600" />
                </div>
              )}
              <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-testid="upload-picture-label">
                {uploadingPicture ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
                <input type="file" accept="image/*" className="hidden" onChange={handlePictureUpload} disabled={uploadingPicture} data-testid="picture-input" />
              </label>
            </div>
            <p className="font-semibold text-emerald-950">{user?.name}</p>
            <p className="text-xs text-stone-500">{user?.email}</p>
            <p className="text-xs text-stone-400 mt-1">Hover over photo to change</p>
          </div>

          {/* Profile Info */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-4" data-testid="profile-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-emerald-950">Profile Information</h3>
              <Button size="sm" variant="outline" onClick={() => setEditing(!editing)} className="rounded-full" data-testid="edit-profile-btn">
                <Edit3 className="w-3.5 h-3.5 mr-1" /> {editing ? "Cancel" : "Edit"}
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-stone-500">Full Name</Label>
                {editing ? <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="name-input" /> : <p className="text-sm font-medium text-emerald-950 mt-1">{user?.name}</p>}
              </div>
              <div>
                <Label className="text-sm text-stone-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
                <p className="text-sm font-medium text-emerald-950 mt-1">{user?.email}</p>
                <p className="text-xs text-stone-400">Email cannot be changed</p>
              </div>
              <div>
                <Label className="text-sm text-stone-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</Label>
                {editing ? <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="phone-input" /> : <p className="text-sm font-medium text-emerald-950 mt-1">{user?.phone || "Not set"}</p>}
              </div>
              {editing && (
                <Button onClick={handleSaveProfile} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full" data-testid="save-profile-btn">
                  <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Changes"}
                </Button>
              )}
            </div>
          </div>

          {/* Password Change */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-4" data-testid="password-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center"><Key className="w-4 h-4 text-blue-600" /></div>
                <div>
                  <h3 className="font-semibold text-emerald-950">Change Password</h3>
                  <p className="text-xs text-stone-500">Update your account password</p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setShowPasswordForm(!showPasswordForm)} className="rounded-full" data-testid="change-password-btn">
                {showPasswordForm ? "Cancel" : "Change"}
              </Button>
            </div>
            {showPasswordForm && (
              <div className="space-y-3 pt-2" data-testid="password-form">
                <div>
                  <Label className="text-sm">Current Password</Label>
                  <Input type="password" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} data-testid="current-password" />
                </div>
                <div>
                  <Label className="text-sm">New Password</Label>
                  <Input type="password" value={passwords.new} onChange={(e) => setPasswords({ ...passwords, new: e.target.value })} data-testid="new-password" />
                </div>
                <div>
                  <Label className="text-sm">Confirm New Password</Label>
                  <Input type="password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} data-testid="confirm-password" />
                </div>
                <Button onClick={handleChangePassword} disabled={changingPassword} className="bg-blue-600 hover:bg-blue-700 text-white w-full" data-testid="submit-password-btn">
                  {changingPassword ? "Changing..." : "Update Password"}
                </Button>
              </div>
            )}
          </div>

          {/* Security */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-4" data-testid="security-section">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center"><Shield className="w-4 h-4 text-emerald-600" /></div>
              <div>
                <h3 className="font-semibold text-emerald-950">Two-Factor Authentication</h3>
                <p className="text-xs text-stone-500">Add an extra layer of security to your account</p>
              </div>
              <Button size="sm" variant="outline" className="ml-auto rounded-full" disabled>Coming Soon</Button>
            </div>
          </div>

          {/* Delete Account */}
          <div className="bg-white rounded-2xl border border-red-100 p-5" data-testid="delete-section">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center"><Trash2 className="w-4 h-4 text-red-600" /></div>
              <div>
                <h3 className="font-semibold text-red-800">Delete Account</h3>
                <p className="text-xs text-stone-500">Permanently delete your account and all data</p>
              </div>
            </div>
            {!showDeleteConfirm ? (
              <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-account-btn">Delete My Account</Button>
            ) : (
              <div className="bg-red-50 rounded-xl p-4" data-testid="delete-confirm">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <p className="text-sm font-medium text-red-800">This action cannot be undone</p>
                </div>
                <p className="text-xs text-red-600 mb-4">All your data, earnings history, documents, and reviews will be permanently deleted.</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="flex-1">Cancel</Button>
                  <Button onClick={handleDeleteAccount} disabled={deleting} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
                    {deleting ? "Deleting..." : "Yes, Delete Account"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AccountManagementPage;
